import React, { useCallback, useMemo } from 'react'
import { intl } from 'lang/intl'
import { FormattedMessage } from 'react-intl'
import { type FormikHelpers, type FormikProps } from 'formik'
import classNames from 'classnames'

import {
  TtButton,
  TtCheckbox,
  TtFlavorContext,
  TtIcon,
  TtMenu,
  TtMenuDivider,
  TtMenuItem,
  TtMenuLabel,
} from '@23/react'

import TtFormik from '../../../../global/formik-forms'
import {
  useIsPanelInitialized,
  usePanel,
} from '../../../../firebase/hooks/usePanel'
import { useHumanParticipants } from '../../../../webrtc/webRTCRoom'
import { useFloorState } from '../../../../firebase/hooks/useFloor'
import { useLastActiveMediaTrayCard } from '../../../../firebase/hooks/useTray'

import './PanelMenuForm.scss'

interface PanelMenuFormProps {
  onSubmit: (
    selectedValues: number[],
    screen: number | 'tray' | undefined
  ) => void
}

type FormValues = {
  [key: string]: boolean | number | string | undefined
}

const PanelMenuForm = ({ onSubmit }: PanelMenuFormProps) => {
  const participants = useHumanParticipants()
  const floor = useFloorState()
  const { mode, participantIds, screen } = usePanel()
  const isPanelInitialized = useIsPanelInitialized()
  const lastActiveMediaTrayCard = useLastActiveMediaTrayCard()

  const listOptions = useMemo(() => {
    if (!isPanelInitialized || !floor) return null
    return participants.map((p) => {
      const selectedIds = mode === 'manual' ? participantIds : null

      const isSelected =
        floor?.layout === 'panel' && mode === 'auto'
          ? true
          : (selectedIds?.includes(p.id) ?? true)

      return {
        label: p.name,
        value: p.id,
        isSelected,
        screenAvailable:
          p.tracks.screen &&
          p.tracks.screen.isEnabled &&
          p.tracks.screen.mediaStream,
        screenSelected: screen === p.id,
      }
    })
  }, [isPanelInitialized, floor, participants, mode, participantIds, screen])

  const initialValues: FormValues = useMemo(() => {
    return (
      listOptions?.reduce(
        (previous, newValue) => {
          return {
            ...previous,
            [`${newValue.value}`]: newValue.isSelected,
          }
        },
        { screen }
      ) ?? {}
    )
  }, [listOptions, screen])

  const onSubmitHandler = (
    values: FormValues,
    _: FormikHelpers<FormValues>
  ) => {
    const ids = Object.entries(values)
      .reduce<number[]>((selectedIds, [id]) => {
        if (id !== 'screen' && values[id]) {
          return [...selectedIds, +id]
        }
        return selectedIds
      }, [])
      .filter((id) => {
        // Check if the participant has a camera track and is enabled
        // If not, we don't want to include them in the selected ids
        const participant = participants?.find((p) => p.id === id)
        return (
          participant &&
          participant.tracks.camera &&
          participant.tracks.camera.isEnabled
        )
      })

    // We need to check if the screen being set is enabled
    let newScreenState = values.screen
    if (Number.isInteger(newScreenState)) {
      const hasScreenEnabled = participants?.find(
        (p) => p.id === newScreenState
      )?.tracks.screen?.isEnabled
      if (!hasScreenEnabled) {
        newScreenState = undefined
      }
    }

    onSubmit(ids, newScreenState as number | 'tray' | undefined)
  }

  const toggleScreen = useCallback(
    async (formik: FormikProps<FormValues>, screenId: number | 'tray') => {
      const newValue = screenId === formik.values.screen ? undefined : screenId
      await formik.setFieldValue('screen', newValue)
    },
    []
  )

  if (!listOptions) return null

  return (
    <TtFormik.Form<FormValues>
      label={intl.formatMessage({ defaultMessage: 'Configure panel form' })}
      className="configure-panel-form"
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmitHandler}
      novalidate
    >
      {(formik) => {
        const formScreenValue = formik.values.screen
        const speakersSelected = Object.keys(formik.values).filter(
          (key) => participants.find((p) => p.id === +key) && formik.values[key]
        )

        const isMixingDisabled =
          !formik.values.screen && speakersSelected.length > 4

        return (
          <TtFlavorContext flavor="webinars">
            <TtMenu className="panel-form-menu">
              <TtMenuLabel className="panel-form-menu-label">
                <FormattedMessage defaultMessage="Configure Panel" />
                <TtButton
                  size="x-small"
                  label={intl.formatMessage({
                    defaultMessage: 'Undo changes',
                  })}
                  color="secondary"
                  onClick={() => formik.resetForm()}
                  className={classNames('undo-changes-btn', {
                    hide: !formik.dirty,
                  })}
                >
                  <TtIcon name="undo" library="one/wysiwyg" />
                </TtButton>
                <TtFormik.SubmitButton
                  label={intl.formatMessage({ defaultMessage: 'Submit' })}
                  size="x-small"
                  color="primary"
                  disabled={false}
                  loading={false}
                >
                  <TtIcon name="check" library="one" />
                </TtFormik.SubmitButton>
              </TtMenuLabel>

              {listOptions.map((option) => {
                const participant = participants?.find(
                  (p) => p.id === option.value
                )

                const hasCamera = participant?.tracks?.camera?.isEnabled
                const hasScreen = participant?.tracks?.screen?.isEnabled

                const isCheckboxDisabled =
                  participant &&
                  !formik.values[participant.id] &&
                  (!hasCamera ||
                    (speakersSelected.length >= 4 && !!formik.values.screen))

                return (
                  <TtMenuItem
                    key={option.value}
                    className="panel-form-menu-item"
                    disabled={isCheckboxDisabled}
                    onTt-click={() => {
                      if (participant && hasCamera && !isCheckboxDisabled) {
                        formik.setFieldValue(
                          `${participant.id}`,
                          !formik.values[participant.id]
                        )
                      }
                    }}
                  >
                    {hasCamera ||
                    (participant && formik.values[participant.id]) ? (
                      <TtCheckbox
                        label={option.label}
                        disabled={isCheckboxDisabled}
                        checked={
                          formik.values[participant.id] ? 'true' : 'false'
                        }
                        onTt-change={() => {
                          formik.setFieldValue(
                            `${participant.id}`,
                            !formik.values[participant.id]
                          )
                        }}
                        slot="prefix"
                      />
                    ) : (
                      <>
                        <TtIcon
                          className="camera-off-icon"
                          library="one/webinars"
                          name="camera-off"
                          slot="prefix"
                        />
                        {option.label}
                      </>
                    )}
                    {hasScreen && (
                      <TtButton
                        slot="suffix"
                        className="panel-participant-screen"
                        color={
                          formScreenValue === option.value
                            ? 'primary'
                            : 'secondary'
                        }
                        variant={isMixingDisabled ? 'ghost' : 'default'}
                        disabled={isMixingDisabled}
                        size="x-small"
                        onClick={() => {
                          if (!isCheckboxDisabled) {
                            formik.setFieldValue(
                              `${participant.id}`,
                              !formik.values[participant.id]
                            )
                          }
                          toggleScreen(formik, option.value)
                        }}
                      >
                        <TtIcon library="one/webinars" name="screen-share" />
                      </TtButton>
                    )}
                  </TtMenuItem>
                )
              })}

              {(screen === 'tray' || !!lastActiveMediaTrayCard) && (
                <>
                  <TtMenuDivider />
                  <TtMenuItem
                    disabled={isMixingDisabled}
                    onTt-click={() => {
                      if (!isMixingDisabled) {
                        toggleScreen(formik, 'tray')
                      }
                    }}
                  >
                    {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                    {}
                    WebinarTray
                    <TtButton
                      slot="suffix"
                      className="panel-participant-tray"
                      variant={isMixingDisabled ? 'ghost' : 'default'}
                      color={
                        formik.values.screen === 'tray'
                          ? 'primary'
                          : 'secondary'
                      }
                      disabled={isMixingDisabled}
                      size="x-small"
                    >
                      <TtIcon library="one/webinars" name="tray-mix-on" />
                    </TtButton>
                  </TtMenuItem>
                </>
              )}
            </TtMenu>
          </TtFlavorContext>
        )
      }}
    </TtFormik.Form>
  )
}

export { PanelMenuForm }
